
import type { NuxtApp } from '#app';
import { GoogleAnalytics } from '~/assets/javascript/modules/analytics/google-analytics';
import { Gtm } from '~/assets/javascript/modules/analytics/gtm';
import { PostHog } from '~/assets/javascript/modules/analytics/posthog';
import { FacebookPixel } from '~/assets/javascript/modules/analytics/facebook-pixel';
import type { PageRouteAlias } from '~/types/pageRoute';
import type { User } from '~/types/user';

const AnalyticsMock = {
  identify: () => { },
  page: () => { },
  track: () => { },
};

class Analytics {
  // eslint-disable-next-line no-use-before-define
  static #instance: Analytics;

  $auth: NuxtApp['$auth'];
  $i18n: NuxtApp['$i18n'];
  googleAnalytics: GoogleAnalytics;
  gtm: Gtm;
  posthog: PostHog;
  facebookPixel: FacebookPixel;

  public static get instance(): Analytics {
    if (!Analytics.#instance) {
      Analytics.#instance = new Analytics();
    }

    return Analytics.#instance;
  }

  constructor() {
    const { $auth, $i18n, $config } = useNuxtApp();

    const isProd = $config.public.deployEnv === 'production';

    this.$auth = $auth;
    this.$i18n = $i18n;
    this.posthog = (isProd ? new PostHog(this.user) : AnalyticsMock) as PostHog;
    this.googleAnalytics = (isProd ? new GoogleAnalytics() : AnalyticsMock) as GoogleAnalytics;
    this.gtm = (isProd ? new Gtm() : AnalyticsMock) as Gtm;
    this.facebookPixel = (isProd ? new FacebookPixel() : AnalyticsMock) as FacebookPixel;
  }

  get connections() {
    return [
      this.posthog,
      this.googleAnalytics,
      this.gtm,
      this.facebookPixel];
  }

  get user(): User | undefined {
    return this.$auth.user;
  }

  get locale() {
    return this.$i18n.locale.value;
  }

  track(eventName: string, properties?: Record<string, any>) {
    this.log('track', eventName, properties);

    [
      this.posthog,
      this.gtm,
      this.facebookPixel,
    ].forEach((connection) => {
      connection.track(eventName, properties);
    });
  }

  page(pageName: string, properties?: Record<string, any>) {
    if (_isEmpty(properties)) this.log('page', pageName);
    else this.log('page', pageName, properties);

    [
      this.posthog,
      this.googleAnalytics,
      this.gtm,
    ].forEach((connection) => {
      connection.page(pageName, properties);
    });
  }

  identify() {
    if (!this.user) return;

    this.log('identify', this.user.username);

    [
      this.posthog,
    ].forEach((connection) => {
      connection.identify(this.user!, this.locale);
    });
  }

  // eslint-disable-next-line class-methods-use-this
  log(functionName: string, ...args: any[]) {
    if (import.meta.dev) {
      // eslint-disable-next-line no-console
      console
        .debug(
          `%cAnalytics %c ${functionName}`,
          'color: white; border-radius: 3px 0 0 3px; padding: 2px 0px 1px 6px; background: #e65100',
          'color: white; border-radius: 0 3px 3px 0; padding: 2px 6px 1px 0px; background: #3F51B5',
          ...args,
        );
    }
  }
}

export const useAnalytics = () => Analytics.instance;

export const useAnalyticsPage = (
  pageName: PageRouteAlias,
  properties: Record<string, any> = {},
  { immediate = false } = {},
) => {
  const analytics = useAnalytics();
  const hook = immediate ? cb => cb() : onNuxtReady;

  hook(() => analytics.page(pageName, properties));
};
